<script setup>
// Renders the difficulty icon for a trail. The shape vocabulary depends
// on the icon set:
//   • mtb     — IMBA shapes (squiggle / leaf / oak / two-leaf), the
//               original set; covers mtb / gravel / fatbike.
//   • alpine  — resort-standard ski signs (green circle, blue square,
//               black diamond, double black, plus a triangle for
//               "pro" and a striped square for the access marker).
//   • nordic  — same shapes as alpine, drawn with white interior +
//               coloured outline so they read against snow.
//   • circle  — plain coloured disc for every level. Hiking, running,
//               equestrian, etc. don't have a codified vocabulary;
//               the colour does the work.
//
// Implementation note: every variant is a flat sibling in the
// v-if / v-else-if chain rather than a nested <template v-if>
// structure so Vue's single-conditional-root class fall-through
// kicks in — passing class="h-6 w-6" from the parent reaches the
// rendered <svg>. Nested templates broke that and the icons rendered
// at 0×0 in the Settings preview.

defineProps({
    difficulty: { type: String, required: true },
    color: { type: String, default: null },
    iconSet: { type: String, default: 'mtb' },
});
</script>

<template>
    <!-- ───────────────── MTB icon set ───────────────── -->
    <svg
        v-if="iconSet === 'mtb' && difficulty === 'access'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 268.37 266.12"
        aria-hidden="true"
    >
        <g fill="currentColor">
            <rect x="12" y="12" width="244.37" height="242.12" rx="46" ry="46"/>
        </g>
        <g fill="none" stroke="#ffffff" stroke-linecap="round" stroke-width="22">
            <line x1="96" y1="60" x2="96" y2="206"/>
            <line x1="172.37" y1="60" x2="172.37" y2="206"/>
        </g>
    </svg>

    <svg
        v-else-if="iconSet === 'mtb' && difficulty === 'easy'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 268.37 266.12"
        aria-hidden="true"
    >
        <path fill="currentColor" d="M177.76,8.08c19.55,6.85,36.9,17.26,51.81,31.71,18.46,17.89,30.12,39.55,35.99,64.45.08.32-.16.72-.35,1.48-3.95-.35-7.96-.69-11.97-1.08-23.45-2.26-45.72,2.76-67.57,10.75-16.19,5.92-30.82,15.03-46.41,22.15-26.28,12.01-53.61,16.31-82.07,9.63-20.23-4.75-38.12-14.57-54.92-26.56-1.62-1.15-2.31-2.4-1.84-4.44,1.05-4.56,1.6-9.26,2.94-13.72,1.67-5.54,2.14-11.37,4.55-16.75,1.17-2.6,3.69-4.53,3.75-7.64,0,0,.29-.16.29-.16l-.05-.32c2.5-3.98,5.03-7.94,6.82-12.31,5.36-6.66,9.97-13.89,15.87-20.15C56.25,22.16,82.54,8.08,113.67,3.03c1.92-.31,3.81-.78,5.71-1.18,0,0,.57.09.57.09l.55-.18c.79-.03,1.58-.06,2.37-.1,5.39-.03,10.85.75,16.06-1.39.5-.21,1.39-.49,1.57-.28,2.66,3.04,6.27.85,9.3,1.77,9.45,1.55,18.69,3.98,27.96,6.31Z"/>
        <path fill="currentColor" d="M55.14,243.03c-.96-1.19-2.26-1.9-3.65-2.46-3.53-3.8-7.8-6.72-11.86-9.89-1.39-1.43-2.5-3.17-4.45-3.98l.05-.12c-7.93-9.09-15.26-18.62-21.24-29.14.03-.99-.41-1.71-1.26-2.2-4.36-10.23-8.56-20.51-10.86-31.45-.88-4.18-1.85-8.35-1.88-12.6,1.99-1.06,3.12.46,4.29,1.21,8.05,5.17,16.63,9.7,25.47,12.85,11.27,4.03,22.89,7.48,34.95,9.03,34.17,4.41,65.93-3.22,95.96-19.06,14.86-7.84,29.78-15.42,45.84-20.53,17.02-5.42,34.34-6.95,52.02-4.14,1.92.31,3.87.5,5.82.59,2.92.13,3.99,1.5,4.02,4.46.12,10.44-1.39,20.66-4.34,30.59-3.31,11.17-7.98,21.66-13.76,31.97-14.21,25.38-34.88,43.16-60.68,55.38-22.16,10.5-45.69,13.98-70.11,12.08-8.56-.67-16.85-2.83-25.35-3.71-1.98-.21-3.09-2.46-5.25-2.22-.64-.68-1.36-1.13-2.35-.91-.93-.27-1.86-.53-2.79-.79-.46-.63-1.09-.83-1.84-.73l-.29-.26s-.39.01-.39.01c-1.64-1.53-3.61-2.27-5.82-2.42,0,0-.29-.13-.29-.13-.22-.6-.69-.72-1.26-.64-.83-.93-1.97-1.23-3.12-1.52-.34-.67-.9-.94-1.63-.86,0,0-.13-.07-.13-.07-.16-.57-.52-.81-1.11-.72-3.06-1.75-6.12-3.49-9.18-5.24-.22-.61-.64-.92-1.3-.88,0,0-.24-.15-.24-.15-.35-.91-1.07-1.28-1.99-1.35Z"/>
    </svg>

    <svg
        v-else-if="iconSet === 'mtb' && difficulty === 'moderate'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 268.05 264.22"
        aria-hidden="true"
    >
        <path fill="currentColor" d="M266.03,50.67c-.29,12.01.44,23.99.96,35.98-.15,2.42-.3,4.83-.49,7.83-3.99-2.62-7.44-5.02-11.01-7.21-16.06-9.83-32.16-9.37-48.22,0-3.85,2.25-7.37,4.96-10.64,7.99-7.79,7.21-15.76,14.25-23.32,21.7-14.59,14.36-30.14,27.71-44.17,42.64-4.23,4.5-8.74,8.75-14.07,11.99-11.08,6.72-20.2,6.26-30.65-1.36-7.13-5.2-12.54-11.95-17.79-18.92-11.49-15.27-23.13-30.43-36.38-44.26-4.79-5.85-10.09-11.17-16.13-15.75-2.78-2.91-5.87-5.43-9.4-7.38-1.63-1.41-3.27-2.82-4.9-4.24.15-8.07.36-16.15-.27-24.21-.02-.86-.04-1.71-.06-2.57.57-5.74.47-11.47-.14-17.2C-.01,28.36-.08,21.02-.74,13.69c.09-2.69.19-5.39.27-8.09C-.35,1.08-.35,1.01,3.93,1c8.48-.03,16.96.04,25.45.04,43.96.01,87.92.03,131.88.02,1.15,0,2.29-.23,3.44-.35,1.19.33,2.38.42,3.57.05,4.62.39,9.23.49,13.85-.04,1.2.34,2.4.28,3.59-.04,4.21.49,8.42.33,12.64.04,2.35.39,4.71.34,7.06-.03,2.67.37,5.34.35,8.01-.01,1.95.06,3.9.11,5.86.17h0c.39,1.17.79,1.16,1.19,0,.38-.08.77-.16,1.15-.25.52.27,1.01.29,1.46-.14l.32.02.14-.29h.02c.72.56,1.39.6,1.97-.2,2.42,1.69,5.04.83,7.61.58.81,0,1.63,0,2.44,0,1.5.44,3.01.42,4.52.05,5.07.38,10.14.59,15.2-.15,2.93.75,6.76-1.14,8.76,1.34,1.7,2.1.63,5.59.75,8.46.44,10.79.76,21.59,1.12,32.39-.39,2.67-.34,5.34.11,8.01Z"/>
        <path fill="currentColor" d="M183.35,263.42c-4.82.06-9.65.13-14.47.19,0,0-.1,0-.1,0-6.77.14-13.55-.28-20.3.56-11.59-.49-23.18-.56-34.77.04-5.77-.83-11.56-.29-17.34-.28-31.66.04-63.33.12-94.99.18-.14-.37-.28-.75-.42-1.12-.01-1.53-.03-3.06-.04-4.59.02-12.1.1-24.19.04-36.29-.08-15.74.02-31.49-.69-47.23.33-.76.32-1.52,0-2.28.34-1.22.32-2.44-.05-3.65.45-1.08.44-2.15,0-3.23.34-1.62.26-3.23-.1-4.83.61-3.44.41-6.88-.01-10.32.47-2.74.37-5.49,0-8.23.29-.78.28-1.55-.08-2.3.55-8.84.16-17.68-.07-26.53,1.75-.76,2.39.77,3.25,1.65,5.46,5.59,10.88,11.23,16.31,16.85,11.39,14.11,22.53,28.41,33.62,42.75,5.92,7.66,13.14,14.17,21.49,19.23,10.73,6.52,22.16,9.42,34.76,6.81,9.27-1.92,17.38-6.03,24.95-11.6,7.69-5.66,13.7-13.01,20.51-19.55,11.94-11.47,24.01-22.81,36.03-34.19,7.71-7.3,15.4-14.63,23.17-21.86,2.39-2.23,4.97-4.28,7.56-6.29,4.72-3.66,9.83-4.1,15.26-1.67,4.07,1.83,7.83,4.15,11.38,6.86,4.29,3.29,8.5,6.72,13.04,9.62,4.17,2.67,7.05,5.78,6.8,11.01-.44,14.63.2,29.25.47,43.87.5,27.14,1.17,54.28,1.74,81.42.09,4.27,0,4.27-4.71,5.03-1.84-.46-3.68-.44-5.52,0-1.21.01-2.41.03-3.62.04-2.58-.39-5.15-.48-7.71.18-.5-.02-1.01-.04-1.51-.06-4.3-.53-8.59-.54-12.89.03-6.22-.46-12.43-.49-18.65,0-.3-.26-.59-.25-.87.04-6.3-.68-12.62-.1-18.93-.23-2.44-6-6.4-6.3-12.12-.49-.13.13-.26.28-.39.41Z"/>
    </svg>

    <svg
        v-else-if="iconSet === 'mtb' && difficulty === 'hard'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 268.37 266.12"
        aria-hidden="true"
    >
        <path fill="currentColor" d="M74.39,208.79c-5.87-7.04-13.01-12.82-19.36-19.38-7.27-7.51-14.67-14.89-22.23-22.54,2.84-3.82,6.48-6.91,9.8-10.29,5.48-5.57,10.48-11.69,16.77-16.35,5.63-4.18,5.86-5.76,9.48,2.45,6.38,14.46,12.54,29.03,18.92,43.49,2.24,5.07,3.78,10.38,5.81,15.52,1.26,3.19,2.56,6.36,4.01,9.46.72,1.54,1.69,2.99,2.73,4.33,3.25,4.17,7.38,6.8,12.84,5.78,5.56-1.03,9.1-4.76,10.74-10.01,1.24-3.99,2.11-8.12,2.81-12.24,1.29-7.51,2.15-15.09,3.52-22.58,1.27-6.92,3.05-13.75,4.51-20.65,1.69-7.97,2.7-16.06,4.76-23.98,4.88-18.74,8.72-37.72,12.79-56.65.47-2.19.91-4.5,2.37-6.39,1.92.43,2.66,2.09,3.57,3.48,9.2,14.08,20.39,26.63,30.59,39.94,2.32,3.02,4.65,6.05,7.12,8.95,1.72,2.02,3.55,4.02,5.59,5.71,8.79,7.27,19.58,5.77,25.51-3.97,3.29-5.41,7.95-9.56,11.63-14.55,1.08-1.46,2.02-1.42,3.21-.18,2.51,2.61,5.07,5.18,7.62,7.75,5.36,5.42,10.74,10.83,16.08,16.27,2.7,2.75,2.72,2.85-.23,5.74-10.77,10.53-21.59,21-32.38,31.5-17.83,17.36-35.64,34.74-53.48,52.09-13.94,13.56-27.88,27.12-41.89,40.6-4.02,3.87-3.3,4.62-7.95.06-12.65-12.41-25.37-24.75-37.98-37.2-1.48-1.46-3.69-2.06-4.65-4.08-3.92-4.34-7.93-8.59-12.65-12.09Z"/>
        <path fill="currentColor" d="M212.69,108.04c-2.26-.88-3.13-2.66-4.28-4.1-8.43-10.57-16.78-21.2-25.21-31.78-4.16-5.22-7.2-11.22-11.57-16.3-2.64-3.07-5.56-5.74-9.11-7.67-7.79-4.23-15.79-2.86-21.8,3.6-1.51,1.62-2.25,4.06-4.95,4.44-.8.11-1.05,1.1-1.14,1.8-1.66,12.99-5.38,25.55-7.98,38.34-4.57,22.58-9.68,45.06-14.58,67.58-1.2,5.51-2.49,11.01-3.83,16.91-2.06-2.45-2.75-5.07-3.78-7.47-5.67-13.1-12.08-25.88-16.99-39.32-1.45-3.98-3.77-7.5-6.46-10.71-6.56-7.81-17.31-9.55-27.05-4.48-5.66,2.95-10.08,7.47-14.5,11.94-6.43,6.51-12.83,13.04-19.2,19.6-1.36,1.4-2.45,2.2-4.2.4-5.02-5.18-10.16-10.25-15.31-15.43,2.29-3.38,5.26-5.79,7.93-8.48,19.6-19.7,39.24-39.38,58.84-59.09,14.37-14.46,28.95-28.72,42.94-43.54,6.49-6.87,13.32-13.4,19.71-20.36,3.82-4.16,4.14-4.14,8.03-.22,12.84,12.94,25.67,25.89,38.5,38.84,14.42,14.56,28.83,29.13,43.25,43.69.54.54,1.1,1.06,1.62,1.61q3.75,3.93.44,8.18c-3.12,4.01-6.25,8.01-9.33,11.96Z"/>
    </svg>

    <svg
        v-else-if="iconSet === 'mtb' && (difficulty === 'expert' || difficulty === 'pro')"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 405.26 275.83"
        aria-hidden="true"
    >
        <path fill="currentColor" d="M400.18,132.13c1.11,1.73,2.44,3.23,4.29,4.19,2.11,3.1-.5,4.61-2.19,6.39-9.65,10.17-19.32,20.3-28.96,30.48-3.36,3.55-6.64,7.19-9.95,10.79-18.32,18.98-36.65,37.96-54.96,56.95-10.54,10.93-21.06,21.88-32.17,33.43-24.86-23.91-47.86-49.28-73-73.34-23.92,24.8-47.66,49.41-72.16,74.81-2.86-3.16-5.42-6.16-8.16-8.97-7.51-7.7-14.91-15.51-22.7-22.91-9.37-8.91-17.84-18.69-27.21-27.56-8.04-7.61-15.31-15.93-23.18-23.7-4.7-4.64-4.85-4.95-.35-9.54,8.77-8.92,17.22-18.21,27.68-26.1,4.67,10.61,9.17,20.81,13.65,31.01,4.69,10.69,9.71,21.23,13.79,32.19,1.47,3.95,3.41,7.7,6.24,10.88,5.49,6.17,13.53,6.06,18.86-.26,2.15-2.54,3.28-5.58,4.32-8.73,5.01-15.17,6.71-31.02,9.34-46.65,4.46-26.49,9.73-52.85,13.11-79.52.32-2.48,1.17-4.89,1.95-8.02,5.38,3.32,8.09,8.26,10.83,12.85,6.7,11.22,12.95,22.72,19.3,34.15,2.84,5.12,5.67,10.23,9.68,14.56,8.71,9.41,20.32,7.1,24.62-4.99,1.32-3.72,1.91-7.7,2.95-11.54,1.71-6.35,4.17-12.42,8.03-17.77,4.01-5.56,6.49-6.13,12.43-2.75,5.88,3.34,11.82,6.56,17.31,10.61,8.12,5.99,16.88,10.96,26.63,13.82,17.68,5.19,33.74,1.72,48.07-9.73,9.5-7.58,18.93-15.24,28.26-23.03,2.55-2.12,4.12-2.6,6.85.08,5.25,5.15,9.13,11.56,15.08,16.03,1.29.97,2.43,2.15,3.63,3.24,2.22,3.33,5.06,6.06,8.09,8.64Z"/>
        <path fill="currentColor" d="M152.03,21.24c-.38,3.35,2.39,4.83,4.2,6.77,7.79,8.32,15.7,16.52,23.56,24.78,6.28,6.6,12.53,13.22,18.8,19.83,4.2,4.42,4.22,4.43,8.42-.12,20.29-22,40.57-44,60.85-66,1.87-2.03,3.76-4.05,6.03-6.5,28.02,29.43,55.67,58.48,83.56,87.78-11.52,9.3-22.44,18.46-33.74,27.11-9.32,7.13-19.81,7.48-30.65,3.69-7.82-2.73-14.67-7.16-21.33-11.95-5.13-3.69-10.39-7.16-16-10.06-16.48-8.5-29.91-4.65-39.24,11.41-3.73,6.42-6.82,13.19-8.85,20.38-2.72-.03-2.92-2.25-3.77-3.73-7.76-13.59-14.91-27.53-23.67-40.54-3.28-4.87-7.04-9.36-11.81-12.84-9.34-6.82-19.09-4.71-24.97,5.2-2.93,4.92-4.47,10.31-5.15,15.89-2.22,18.32-5.82,36.42-8.72,54.63-3.08,19.35-6.62,38.62-10,58.1-2.4-1.13-2.64-3.43-3.42-5.25-7.23-16.97-15.29-33.58-22-50.79-5.89-15.12-19.59-14.86-28.85-7.01-2.14,1.81-4.14,3.77-6.15,5.73-8.21,7.98-16.53,15.86-24.52,24.06-2.8,2.88-4.48,2.3-6.62-.38-7.74-9.69-16.95-17.98-25.55-26.85-3.28-3.38-3.21-3.93.03-7.39C41.61,95.4,80.76,53.61,119.92,11.84c3.33-3.55,6.19-7.58,9.66-10.08,5.81,5.49,11.33,10.83,17.02,16,1.58,1.43,2.4,4.29,5.45,3.51v-.02Z"/>
    </svg>

    <!-- ───────────────── Alpine ski icon set ───────────────── -->
    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'easy'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="10" fill="currentColor" />
    </svg>

    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'moderate'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="18" height="18" fill="currentColor" />
    </svg>

    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'hard'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <polygon points="12,1.5 22.5,12 12,22.5 1.5,12" fill="currentColor" />
    </svg>

    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'expert'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <g fill="currentColor">
            <polygon points="6,2 11,12 6,22 1,12" />
            <polygon points="18,2 23,12 18,22 13,12" />
        </g>
    </svg>

    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'pro'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <polygon points="12,2 22.5,21 1.5,21" fill="currentColor" />
    </svg>

    <svg
        v-else-if="iconSet === 'alpine' && difficulty === 'access'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <rect x="3" y="3" width="18" height="18" fill="currentColor" />
        <g fill="none" stroke="#ffffff" stroke-linecap="round" stroke-width="2">
            <line x1="9" y1="6.5" x2="9" y2="17.5" />
            <line x1="15" y1="6.5" x2="15" y2="17.5" />
        </g>
    </svg>

    <!-- ───────────────── Nordic ski icon set ───────────────── -->
    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'easy'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="9" fill="#ffffff" stroke="currentColor" stroke-width="3" />
    </svg>

    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'moderate'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <rect x="4" y="4" width="16" height="16" fill="#ffffff" stroke="currentColor" stroke-width="3" />
    </svg>

    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'hard'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <polygon points="12,2.5 21.5,12 12,21.5 2.5,12" fill="#ffffff" stroke="currentColor" stroke-width="3" stroke-linejoin="round" />
    </svg>

    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'expert'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <g fill="#ffffff" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round">
            <polygon points="6,2.5 10.4,12 6,21.5 1.6,12" />
            <polygon points="18,2.5 22.4,12 18,21.5 13.6,12" />
        </g>
    </svg>

    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'pro'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <polygon points="12,2.5 21.5,20.5 2.5,20.5" fill="#ffffff" stroke="currentColor" stroke-width="3" stroke-linejoin="round" />
    </svg>

    <svg
        v-else-if="iconSet === 'nordic' && difficulty === 'access'"
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <rect x="4" y="4" width="16" height="16" fill="#ffffff" stroke="currentColor" stroke-width="3" />
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
            <line x1="9" y1="7.5" x2="9" y2="16.5" />
            <line x1="15" y1="7.5" x2="15" y2="16.5" />
        </g>
    </svg>

    <!-- ───────────────── Generic / circle icon set ───────────────── -->
    <!-- Hiking, running, equestrian, snowshoeing, etc. don't have a
         widely-codified shape vocabulary. The colour is the entire
         signal here, so every difficulty level renders as the same
         disc — also the fallback for any unknown iconSet value. -->
    <svg
        v-else
        :style="color ? { color } : {}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="10" fill="currentColor" />
    </svg>
</template>
